import { track, type BeforeSendEvent } from '@vercel/analytics';

// Finite dimensions only: no identifiers, free text, amounts, URLs, or raw errors.
const PROPERTY_VALUES = {
  intent: ['post', 'earn', 'sell'],
  source: ['landing_hero', 'landing_empty', 'header', 'post', 'auth', 'subscription', 'subscription_modal', 'sell'],
  auth_state: ['anonymous', 'authenticated'],
  mode: ['signup', 'signin', 'recovery'],
  plan: ['free', 'pro'],
  method: ['wallet', 'card'],
  credit_type: ['none', 'intro', 'backer'],
  listing_count: ['one', 'multiple'],
  stage: ['draft', 'signup', 'confirmation', 'signin', 'mfa', 'checkout', 'publication'],
  error_category: ['validation', 'network', 'rate_limited', 'expired_link', 'invalid_credentials', 'unconfirmed', 'mfa_required', 'canceled', 'unavailable', 'quote_expired', 'quote_changed', 'unknown'],
} as const;

const EVENT_PROPERTIES = {
  entry_intent: ['intent', 'source'],
  draft_started: ['source', 'auth_state'],
  draft_restored: ['source', 'auth_state'],
  auth_requested: ['source', 'intent', 'mode'],
  signup_requested: ['source', 'intent'],
  confirmation_sent: ['source', 'mode'],
  email_confirmed: ['source'],
  confirmation_resent: ['source', 'mode'],
  auth_error: ['source', 'mode', 'stage', 'error_category'],
  checkout_started: ['source', 'plan', 'method', 'credit_type', 'listing_count'],
  checkout_canceled: ['source', 'method'],
  checkout_error: ['source', 'method', 'stage', 'error_category'],
  publication_confirmed: ['source', 'plan', 'method', 'credit_type', 'listing_count'],
} as const satisfies Record<string, readonly (keyof typeof PROPERTY_VALUES)[]>;

export type FunnelEventName = keyof typeof EVENT_PROPERTIES;
export type FunnelEventProps<Name extends FunnelEventName = FunnelEventName> = {
  [Key in (typeof EVENT_PROPERTIES)[Name][number]]?: (typeof PROPERTY_VALUES)[Key][number];
};

function isFunnelEventName(name: unknown): name is FunnelEventName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(EVENT_PROPERTIES, name);
}

function boundedProperties(name: FunnelEventName, input: unknown): Record<string, string> {
  const result: Record<string, string> = {};
  if (!input || typeof input !== 'object' || Array.isArray(input)) return result;
  for (const key of EVENT_PROPERTIES[name]) {
    // Ignore inherited properties and accessors rather than evaluating arbitrary input.
    const value: unknown = Object.getOwnPropertyDescriptor(input, key)?.value;
    if (typeof value === 'string' && (PROPERTY_VALUES[key] as readonly string[]).includes(value)) {
      result[key] = value;
    }
  }
  return result;
}

/** Directional analytics only; confirmed publication must come from committed server state. */
export function trackFunnelEvent<Name extends FunnelEventName>(
  name: Name,
  properties: FunnelEventProps<Name> = {},
): void {
  try {
    if (typeof window === 'undefined' || !isFunnelEventName(name)) return;
    track(name, boundedProperties(name, properties));
  } catch {
    // Analytics must never interrupt drafting, authentication, or payments.
  }
}

const ANALYTICS_ORIGIN = 'https://www.bountyclaimer.com';
const STATIC_PATHS = new Set([
  '/', '/post', '/auth', '/profile', '/subscription', '/analytics', '/messages',
  '/deposit', '/settings', '/onboarding', '/wall', '/founders-wall', '/redeem',
  '/admin', '/ops-command', '/other',
]);
const DYNAMIC_PATHS: Record<string, string> = {
  bounty: '/bounty/:id',
  edit: '/edit/:id',
  profile: '/profile/:userId',
  wall: '/wall/:slug',
};

/** Returns only a known route path; origins, queries, fragments, and dynamic values are discarded. */
export function sanitizeAnalyticsUrl(input: unknown): string {
  if (typeof input !== 'string' || input.length > 8192 || input !== input.trim()) return '/other';
  if ((!input.startsWith('/') && !/^https?:\/\//i.test(input)) || input.startsWith('//') || input.includes('\\')) return '/other';
  try {
    const url = new URL(input, ANALYTICS_ORIGIN);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '/other';
    const path = decodeURIComponent(url.pathname).replace(/\/+$/, '') || '/';
    if (STATIC_PATHS.has(path)) return path;
    const segments = path.split('/');
    if (segments.length === 3 && segments[2] && Object.prototype.hasOwnProperty.call(DYNAMIC_PATHS, segments[1])) {
      return DYNAMIC_PATHS[segments[1]];
    }
  } catch {
    // Malformed and unrecognized routes have one bounded fallback.
  }
  return '/other';
}

/** Shared beforeSend hook for both pageviews and custom-event URL metadata. */
export function sanitizeAnalyticsEvent(event: BeforeSendEvent): BeforeSendEvent | null {
  try {
    if (!event || (event.type !== 'pageview' && event.type !== 'event')) return null;
    // Rebuild, never spread input: discard referrers, titles, IDs, and future metadata.
    const clean: BeforeSendEvent = {
      type: event.type,
      url: `${ANALYTICS_ORIGIN}${sanitizeAnalyticsUrl(event.url)}`,
    };
    // The installed SDK declares only type/url. Also defend SDKs exposing name/data.
    if (event.type === 'event' && Object.prototype.hasOwnProperty.call(event, 'name')) {
      const metadata = event as BeforeSendEvent & { name: unknown; data?: unknown };
      if (!isFunnelEventName(metadata.name)) return null;
      return Object.assign(clean, { name: metadata.name, data: boundedProperties(metadata.name, metadata.data) });
    }
    return clean;
  } catch {
    return null;
  }
}
